import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, DestroyRef, ElementRef, HostListener, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter, firstValueFrom } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import * as L from 'leaflet';
import { MatButtonModule } from '@angular/material/button';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar, MatSnackBarHorizontalPosition, MatSnackBarVerticalPosition } from '@angular/material/snack-bar';
import { AuthorizedApiService, CanAccessDirective } from '@core';
import { AuthorizationService } from '@core/service/authorization.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BreadcrumbComponent } from '@shared/components/breadcrumb/breadcrumb.component';
import { ConfirmDeleteDialogComponent } from '@shared/components/confirm-delete-dialog/confirm-delete-dialog.component';
import { LoadingStateComponent } from '@shared/components/loading-state/loading-state.component';
import { extractApiErrorMessage } from '@shared/utils/api-error.util';
import { EmployeeRecord } from '../../organization/employees/employee.interface';
import { AttendanceEventCreatePayload, AttendanceEventRecord } from './attendance-event.interface';
import { TrackingRouteRecord, TrackingRoutesResponse } from '../tracking-point/tracking-point.interface';

type AttendanceEventWorkspaceMode = 'view' | 'create';

interface EmployeesResponse {
  data?: EmployeeRecord[];
  items?: EmployeeRecord[];
  results?: EmployeeRecord[];
  employees?: EmployeeRecord[];
}

interface AttendanceEventFormValue {
  employeeId: string;
  source: string;
  eventType: string;
  normalizedEventType: string;
  status: string;
  eventAt: string;
  timezone: string;
  deviceId: string;
  deviceVersion: string;
  lat: number | '';
  lng: number | '';
  accuracy: number | '';
  faceValidationStatus: string;
  rawPayloadText: string;
  isActive: boolean;
}

interface AttendanceEventCalendarDay {
  date: Date;
  label: number;
  currentMonth: boolean;
  selected: boolean;
  today: boolean;
}

@Component({
  selector: 'app-attendance-event-detail',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    BreadcrumbComponent,
    LoadingStateComponent,
    CanAccessDirective,
    MatAutocompleteModule,
    MatButtonModule,
    MatCardModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    TranslateModule,
  ],
  templateUrl: './attendance-event-detail.component.html',
  styleUrl: './attendance-event-detail.component.scss',
})
export class AttendanceEventDetailComponent implements OnInit, AfterViewInit, OnDestroy {
  private static readonly RELATED_TRACKING_WINDOW_MS = 60 * 60 * 1000;
  private static readonly RELATED_ROUTE_COLOR = '#2563eb';
  private static readonly EVENT_MARKER_COLOR = '#ef4444';

  @ViewChild('relatedTrackingMap') private relatedTrackingMapRef?: ElementRef<HTMLDivElement>;
  @ViewChild('eventAtPickerShell') private eventAtPickerShellRef?: ElementRef<HTMLDivElement>;

  private readonly apiService = inject(AuthorizedApiService);
  private readonly authorizationService = inject(AuthorizationService);
  private readonly dialog = inject(MatDialog);
  private readonly snackBar = inject(MatSnackBar);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  isLoading = true;
  isSaving = false;
  isDeleting = false;
  isRelatedTrackingLoading = false;
  mode: AttendanceEventWorkspaceMode = 'view';
  employees: EmployeeRecord[] = [];
  selectedEventId: string | null = null;
  selectedEvent: AttendanceEventRecord | null = null;
  form: AttendanceEventFormValue = this.createEmptyForm();
  employeeSearchText: string | EmployeeRecord = '';
  relatedTrackingRoutes: TrackingRouteRecord[] = [];
  relatedTrackingRangeLabel = '';
  eventAtInput = '';
  isEventAtPickerOpen = false;
  eventAtVisibleMonth = this.startOfMonth(new Date());
  private employeeSearchDebounceHandle: ReturnType<typeof setTimeout> | null = null;

  private relatedTrackingMap: L.Map | null = null;
  private relatedTrackingTileLayer: L.TileLayer | null = null;
  private relatedTrackingLayers: L.Layer[] = [];
  private readonly defaultMapCenter: L.LatLngTuple = [21.1619, -86.8515];

  readonly readAccess = { resource: 'attendanceEvents', action: 'read' } as const;
  readonly createAccess = { resource: 'attendanceEvents', action: 'create' } as const;
  readonly deleteAccess = { resource: 'attendanceEvents', action: 'delete' } as const;

  readonly sourceOptions = ['device', 'mobile', 'web', 'manual'];
  readonly eventTypeOptions = ['entry', 'exit', 'meal_start', 'meal_end', 'break_start', 'break_end', 'unknown'];
  readonly statusOptions = ['pending_analysis', 'analyzed', 'rejected'];
  readonly faceValidationStatusOptions = ['passed', 'failed', 'pending', 'not_required'];

  ngOnInit(): void {
    this.translate.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (this.selectedEvent) {
        this.selectedEvent = this.normalizeEvent(this.selectedEvent);
        const range = this.buildRelatedTrackingRange(this.selectedEvent.eventAt);
        this.relatedTrackingRangeLabel = range
          ? `${this.formatDateTime(range.dateFrom.toISOString())} - ${this.formatDateTime(range.dateTo.toISOString())}`
          : '';
        this.renderRelatedTrackingMap();
      }
    });

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        void this.syncWithRoute();
      });

    void this.loadBaseData().then(() => this.syncWithRoute());
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.renderRelatedTrackingMap(), 0);
  }

  ngOnDestroy(): void {
    this.relatedTrackingLayers = [];
    if (this.relatedTrackingMap) {
      this.relatedTrackingMap.remove();
      this.relatedTrackingMap = null;
    }
  }

  get isCreateMode(): boolean {
    return this.mode === 'create';
  }

  get isViewMode(): boolean {
    return this.mode === 'view';
  }

  get detailTitle(): string {
    if (this.isCreateMode) {
      return this.t('attendanceEventsCatalog.detail.createTitle');
    }
    return this.t('attendanceEventsCatalog.detail.title');
  }

  get detailDescription(): string {
    if (this.isCreateMode) {
      return this.t('attendanceEventsCatalog.detail.createDescription');
    }
    return this.t('attendanceEventsCatalog.detail.description');
  }

  get canSave(): boolean {
    return !this.validateForm();
  }

  get filteredEmployees(): EmployeeRecord[] {
    return this.employees;
  }

  readonly displayEmployeeOption = (value: string | EmployeeRecord | null): string => this.getEmployeeSearchText(value);

  get hasRelatedTracking(): boolean {
    return this.relatedTrackingRoutes.some((route) => route.points.length > 0);
  }

  get hasEventLocation(): boolean {
    return this.isFiniteNumber(this.selectedEvent?.lat) && this.isFiniteNumber(this.selectedEvent?.lng);
  }

  get hasTrackingMapData(): boolean {
    return this.hasEventLocation || this.hasRelatedTracking;
  }

  get hasRawPayload(): boolean {
    const rawPayload = this.selectedEvent?.rawPayload;
    return rawPayload !== null && rawPayload !== undefined && Object.keys(rawPayload).length > 0;
  }

  get eventCoordinatesLabel(): string {
    if (!this.hasEventLocation || !this.selectedEvent) {
      return '-';
    }

    return `${this.selectedEvent.lat!.toFixed(6)}, ${this.selectedEvent.lng!.toFixed(6)}`;
  }

  get eventAccuracyLabel(): string {
    if (!this.isFiniteNumber(this.selectedEvent?.accuracy)) {
      return '-';
    }

    return `${this.selectedEvent!.accuracy} m`;
  }

  get employeeInitials(): string {
    const name = this.selectedEvent?.employeeLabel || this.selectedEvent?.employeeName || '';
    const initials = name
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part.charAt(0).toUpperCase())
      .join('');
    return initials || '—';
  }

  get eventDateLabel(): string {
    return this.formatEventDatePart('date');
  }

  get eventTimeLabel(): string {
    return this.formatEventDatePart('time');
  }

  get eventSourceIcon(): string {
    switch (`${this.selectedEvent?.source || ''}`.toLowerCase()) {
      case 'mobile': return 'smartphone';
      case 'web': return 'language';
      case 'manual': return 'edit_note';
      case 'kiosk': return 'point_of_sale';
      default: return 'devices';
    }
  }

  get eventTypeIcon(): string {
    const eventType = `${this.selectedEvent?.normalizedEventType || this.selectedEvent?.eventType || ''}`.toLowerCase();
    if (eventType.includes('exit') || eventType.includes('salida')) {
      return 'logout';
    }
    if (eventType.includes('meal')) {
      return 'restaurant';
    }
    if (eventType.includes('break')) {
      return 'coffee';
    }
    return 'login';
  }

  get isFaceValidationSuccessful(): boolean {
    return ['passed', 'approved', 'aprobada'].includes(`${this.selectedEvent?.faceValidationStatus || ''}`.toLowerCase());
  }

  get googleMapsUrl(): string | null {
    if (!this.hasEventLocation || !this.selectedEvent) {
      return null;
    }
    return `https://www.google.com/maps/search/?api=1&query=${this.selectedEvent.lat},${this.selectedEvent.lng}`;
  }

  get relatedTrackingPointsCount(): number {
    return this.relatedTrackingRoutes.reduce((total, route) => total + route.points.length, 0);
  }

  get eventAtCalendarDays(): AttendanceEventCalendarDay[] {
    const monthStart = this.startOfMonth(this.eventAtVisibleMonth);
    const firstWeekdayOffset = (monthStart.getDay() + 6) % 7;
    const calendarStart = new Date(monthStart);
    calendarStart.setDate(monthStart.getDate() - firstWeekdayOffset);

    const selectedDate = this.getSelectedEventAtDate();
    const today = this.startOfDay(new Date());

    return Array.from({ length: 42 }, (_, index) => {
      const date = new Date(calendarStart);
      date.setDate(calendarStart.getDate() + index);
      return {
        date,
        label: date.getDate(),
        currentMonth: date.getMonth() === monthStart.getMonth(),
        selected: !!selectedDate && this.isSameDay(date, selectedDate),
        today: this.isSameDay(date, today),
      };
    });
  }

  get eventAtMonthLabel(): string {
    const locale = this.translate.currentLang?.startsWith('en') ? 'en-US' : 'es-MX';
    return new Intl.DateTimeFormat(locale, {
      month: 'short',
      year: 'numeric',
    }).format(this.eventAtVisibleMonth);
  }

  get eventAtWeekdayLabels(): string[] {
    return this.translate.currentLang?.startsWith('en')
      ? ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN']
      : ['LUN', 'MAR', 'MIÉ', 'JUE', 'VIE', 'SÁB', 'DOM'];
  }

  get eventAtTimeValue(): string {
    const parsedDate = this.parseEventAtInput(this.eventAtInput) || this.parseIsoDate(this.form.eventAt);
    return parsedDate ? this.toTimeString(parsedDate) : '08:00';
  }

  goBackToList(): void {
    void this.router.navigate(['/attendance/attendance-events'], { queryParamsHandling: 'preserve' });
  }

  cancelEdit(): void {
    if (this.selectedEvent) {
      this.mode = 'view';
      this.form = this.createFormFromEvent(this.selectedEvent);
      this.syncEventAtInputFromForm();
      void this.loadRelatedTracking(this.selectedEvent);
      void this.router.navigate(['/attendance/attendance-events', this.selectedEvent.attendanceEventId], { queryParamsHandling: 'preserve' });
      return;
    }

    this.goBackToList();
  }

  async save(): Promise<void> {
    const validationMessage = this.validateForm();
    if (validationMessage) {
      this.showNotification('snackbar-warning', validationMessage, 'bottom', 'center');
      return;
    }

    this.isSaving = true;
    try {
      if (this.isCreateMode) {
        const payload = this.buildCreatePayload();
        const created = await firstValueFrom(this.apiService.post<AttendanceEventRecord>('attendance/events', payload));
        this.showNotification('snackbar-success', this.t('attendanceEventsCatalog.notifications.created'), 'bottom', 'center');
        void this.router.navigate(['/attendance/attendance-events', created.attendanceEventId], { queryParamsHandling: 'preserve' });
        return;
      }

    } catch (error) {
      console.error('Error saving attendance event', error);
      this.showNotification(
        'snackbar-danger',
        extractApiErrorMessage(error, this.t('attendanceEventsCatalog.notifications.saveError')),
        'bottom',
        'center'
      );
    } finally {
      this.isSaving = false;
    }
  }

  deleteSelected(): void {
    if (!this.selectedEvent || !this.hasAccess('delete')) {
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDeleteDialogComponent, {
      width: '420px',
      data: {
        title: this.t('attendanceEventsCatalog.dialog.deleteTitle'),
        message: this.t('attendanceEventsCatalog.dialog.deleteMessage', { event: this.selectedEvent.employeeNumber }),
        confirmLabel: this.t('attendanceEventsCatalog.dialog.deleteConfirm'),
        cancelLabel: this.t('common.cancel'),
      },
    });

    dialogRef.afterClosed().subscribe(async (confirmed: boolean) => {
      if (!confirmed || !this.selectedEventId) {
        return;
      }

      this.isDeleting = true;
      try {
        await firstValueFrom(this.apiService.delete(`attendance/events/${this.selectedEventId}`));
        this.showNotification('snackbar-success', this.t('attendanceEventsCatalog.notifications.deleted'), 'bottom', 'center');
        void this.router.navigate(['/attendance/attendance-events'], { queryParamsHandling: 'preserve' });
      } catch (error) {
        console.error('Error deleting attendance event', error);
        this.showNotification(
          'snackbar-danger',
          extractApiErrorMessage(error, this.t('attendanceEventsCatalog.notifications.deleteError')),
          'bottom',
          'center'
        );
      } finally {
        this.isDeleting = false;
      }
    });
  }

  private async loadBaseData(): Promise<void> {
    this.isLoading = true;
    try {
      await this.loadEmployees();
    } catch (error) {
      console.error('Error loading employees for attendance events', error);
      this.employees = [];
    } finally {
      this.isLoading = false;
    }
  }

  private async syncWithRoute(): Promise<void> {
    const attendanceEventId = this.route.snapshot.paramMap.get('attendanceEventId');
    const currentUrl = this.router.url.split('?')[0];
    const isCreateRoute = currentUrl.endsWith('/attendance/attendance-events/new');

    if (isCreateRoute) {
      this.enterCreateMode();
      return;
    }

    if (!attendanceEventId) {
      void this.router.navigate(['/attendance/attendance-events'], { queryParamsHandling: 'preserve' });
      return;
    }

    this.isLoading = true;
    try {
      const event = await firstValueFrom(this.apiService.get<AttendanceEventRecord>(`attendance/events/${attendanceEventId}`));
      this.selectedEvent = this.normalizeEvent(event);
      this.selectedEventId = this.selectedEvent.attendanceEventId;
      this.form = this.createFormFromEvent(this.selectedEvent);
      this.syncEventAtInputFromForm();
      this.mode = 'view';
      await this.loadRelatedTracking(this.selectedEvent);
    } catch (error) {
      console.error('Error loading attendance event detail', error);
      this.showNotification(
        'snackbar-danger',
        extractApiErrorMessage(error, this.t('attendanceEventsCatalog.notifications.loadDetailError')),
        'bottom',
        'center'
      );
      void this.router.navigate(['/attendance/attendance-events']);
    } finally {
      this.isLoading = false;
    }
  }

  private enterCreateMode(): void {
    this.mode = 'create';
    this.selectedEvent = null;
    this.selectedEventId = null;
    this.form = this.createEmptyForm();
    this.employeeSearchText = '';
    this.syncEventAtInputFromForm();
    this.clearRelatedTracking();
    this.isLoading = false;
  }

  private async loadRelatedTracking(event: AttendanceEventRecord): Promise<void> {
    const range = this.buildRelatedTrackingRange(event.eventAt);
    if (!event.employeeId || !range) {
      this.clearRelatedTracking();
      return;
    }

    this.isRelatedTrackingLoading = true;
    this.relatedTrackingRangeLabel = `${this.formatDateTime(range.dateFrom.toISOString())} - ${this.formatDateTime(range.dateTo.toISOString())}`;
    try {
      const response = await firstValueFrom(
        this.apiService.get<TrackingRoutesResponse>('attendance/tracking-routes', {
          params: {
            employeeId: event.employeeId,
            dateFrom: range.dateFrom.toISOString(),
            dateTo: range.dateTo.toISOString(),
          },
        })
      );

      this.relatedTrackingRoutes = (response?.data || []).map((group) => ({
        ...group,
        points: [...(group.points || [])].sort((left, right) => left.trackedAt.localeCompare(right.trackedAt)),
      }));
      setTimeout(() => this.renderRelatedTrackingMap(), 0);
    } catch (error) {
      console.error('Error loading related tracking for attendance event', error);
      this.relatedTrackingRoutes = [];
      setTimeout(() => this.renderRelatedTrackingMap(), 0);
      this.showNotification(
        'snackbar-danger',
        extractApiErrorMessage(error, this.t('attendanceEventsCatalog.notifications.loadTrackingError')),
        'bottom',
        'center'
      );
    } finally {
      this.isRelatedTrackingLoading = false;
    }
  }

  private clearRelatedTracking(): void {
    this.relatedTrackingRoutes = [];
    this.relatedTrackingRangeLabel = '';
    setTimeout(() => this.renderRelatedTrackingMap(), 0);
  }

  private createEmptyForm(): AttendanceEventFormValue {
    return {
      employeeId: '',
      source: 'manual',
      eventType: 'entrada',
      normalizedEventType: '',
      status: 'pending_analysis',
      eventAt: new Date().toISOString(),
      timezone: 'America/Cancun',
      deviceId: '',
      deviceVersion: '',
      lat: '',
      lng: '',
      accuracy: '',
      faceValidationStatus: '',
      rawPayloadText: '{\n  "deviceEventId": ""\n}',
      isActive: true,
    };
  }

  private createFormFromEvent(event: AttendanceEventRecord): AttendanceEventFormValue {
    return {
      employeeId: event.employeeId,
      source: event.source,
      eventType: event.eventType || '',
      normalizedEventType: event.normalizedEventType || '',
      status: event.status || '',
      eventAt: event.eventAt || '',
      timezone: event.timezone || 'America/Cancun',
      deviceId: event.deviceId || '',
      deviceVersion: event.deviceVersion || '',
      lat: event.lat ?? '',
      lng: event.lng ?? '',
      accuracy: event.accuracy ?? '',
      faceValidationStatus: event.faceValidationStatus || '',
      rawPayloadText: JSON.stringify(event.rawPayload || {}, null, 2),
      isActive: event.isActive !== false,
    };
  }

  onEmployeeSearchInput(value: string | EmployeeRecord): void {
    const searchText = this.getEmployeeSearchText(value);
    this.employeeSearchText = value;
    const selectedEmployee = this.employees.find((employee) => employee.employeeId === this.form.employeeId);
    if (selectedEmployee && (selectedEmployee.fullName || selectedEmployee.employeeNumber || '').trim() !== searchText.trim()) {
      this.form.employeeId = '';
    }

    if (this.employeeSearchDebounceHandle) {
      clearTimeout(this.employeeSearchDebounceHandle);
    }

    this.employeeSearchDebounceHandle = setTimeout(() => {
      void this.loadEmployees(searchText);
    }, 250);
  }

  onEmployeeSelected(event: MatAutocompleteSelectedEvent): void {
    const employee = event.option.value as EmployeeRecord | null;
    this.form.employeeId = employee?.employeeId || '';
    this.employeeSearchText = employee || '';
  }

  openEventAtPicker(): void {
    this.isEventAtPickerOpen = true;
    const selectedDate = this.getSelectedEventAtDate();
    this.eventAtVisibleMonth = this.startOfMonth(selectedDate || new Date());
  }

  closeEventAtPicker(): void {
    this.isEventAtPickerOpen = false;
  }

  onEventAtInputChange(value: string): void {
    this.eventAtInput = value;
    const parsedDate = this.parseEventAtInput(value);
    if (!parsedDate) {
      this.form.eventAt = '';
      return;
    }

    this.form.eventAt = parsedDate.toISOString();
    this.eventAtVisibleMonth = this.startOfMonth(parsedDate);
  }

  selectEventAtDate(date: Date): void {
    const nextDate = new Date(date);
    const [hours, minutes] = this.getEventAtTimeParts();
    nextDate.setHours(hours, minutes, 0, 0);
    this.form.eventAt = nextDate.toISOString();
    this.syncEventAtInputFromForm();
  }

  onEventAtTimeChange(value: string): void {
    const [hoursText, minutesText] = value.split(':');
    const hours = Number(hoursText);
    const minutes = Number(minutesText);
    const baseDate = this.getSelectedEventAtDate() || new Date();
    const nextDate = new Date(baseDate);
    nextDate.setHours(Number.isFinite(hours) ? hours : 0, Number.isFinite(minutes) ? minutes : 0, 0, 0);
    this.form.eventAt = nextDate.toISOString();
    this.syncEventAtInputFromForm();
  }

  goToTodayEventAt(): void {
    const now = new Date();
    this.form.eventAt = now.toISOString();
    this.syncEventAtInputFromForm();
  }

  changeEventAtMonth(offset: number): void {
    const nextMonth = new Date(this.eventAtVisibleMonth);
    nextMonth.setMonth(nextMonth.getMonth() + offset);
    this.eventAtVisibleMonth = this.startOfMonth(nextMonth);
  }

  private buildCreatePayload(): AttendanceEventCreatePayload {
    return {
      employeeId: this.form.employeeId,
      source: this.form.source,
      eventType: this.form.eventType,
      eventAt: this.form.eventAt,
      timezone: this.form.timezone.trim(),
      ...(this.form.deviceId.trim() ? { deviceId: this.form.deviceId.trim() } : {}),
      ...(this.form.deviceVersion.trim() ? { deviceVersion: this.form.deviceVersion.trim() } : {}),
      ...(this.form.lat !== '' ? { lat: Number(this.form.lat) } : {}),
      ...(this.form.lng !== '' ? { lng: Number(this.form.lng) } : {}),
      ...(this.form.accuracy !== '' ? { accuracy: Number(this.form.accuracy) } : {}),
      ...(this.form.faceValidationStatus ? { faceValidationStatus: this.form.faceValidationStatus } : {}),
      ...(this.parseRawPayload() ? { rawPayload: this.parseRawPayload()! } : {}),
    };
  }

  private validateForm(): string | null {
    if (!this.form.employeeId) {
      return this.t('attendanceEventsCatalog.notifications.validationEmployee');
    }
    if (!this.form.source) {
      return this.t('attendanceEventsCatalog.notifications.validationSource');
    }
    if (!this.form.eventType) {
      return this.t('attendanceEventsCatalog.notifications.validationEventType');
    }
    if (!this.form.eventAt) {
      return this.t('attendanceEventsCatalog.notifications.validationEventAt');
    }
    if (!this.form.timezone.trim()) {
      return this.t('attendanceEventsCatalog.notifications.validationTimezone');
    }
    if (this.form.rawPayloadText.trim()) {
      try {
        JSON.parse(this.form.rawPayloadText);
      } catch {
        return this.t('attendanceEventsCatalog.notifications.validationRawPayload');
      }
    }
    return null;
  }

  private parseRawPayload(): Record<string, unknown> | null {
    if (!this.form.rawPayloadText.trim()) {
      return null;
    }
    return JSON.parse(this.form.rawPayloadText);
  }

  private extractEmployees(response: EmployeeRecord[] | EmployeesResponse): EmployeeRecord[] {
    if (Array.isArray(response)) {
      return response;
    }
    return response.items || response.data || response.results || response.employees || [];
  }

  private async loadEmployees(search = ''): Promise<void> {
    try {
      const selectedEmployeeId = this.form.employeeId;
      const selectedEmployee = this.employees.find((employee) => employee.employeeId === selectedEmployeeId);
      const response = await firstValueFrom(
        this.apiService.get<EmployeeRecord[] | EmployeesResponse>('employees', {
          params: { page: 1, perPage: 20, isActive: 'true', ...(search ? { search } : {}) },
        })
      );

      const nextEmployees = this.extractEmployees(response)
        .filter((employee) => employee.isActive !== false)
        .sort((left, right) => (left.fullName || '').localeCompare(right.fullName || ''));

      this.employees =
        selectedEmployee && !nextEmployees.some((employee) => employee.employeeId === selectedEmployee.employeeId)
          ? [selectedEmployee, ...nextEmployees]
          : nextEmployees;

      if (selectedEmployeeId) {
        const refreshedSelectedEmployee = this.employees.find((employee) => employee.employeeId === selectedEmployeeId);
        this.employeeSearchText = refreshedSelectedEmployee || this.getEmployeeSearchText(this.employeeSearchText);
      }
    } catch (error) {
      console.error('Error loading employees for attendance events', error);
      this.employees = [];
    }
  }

  private getEmployeeSearchText(value: string | EmployeeRecord | null | undefined): string {
    if (!value) {
      return '';
    }

    if (typeof value === 'string') {
      return value;
    }

    return value.fullName || value.employeeNumber || '';
  }

  private normalizeEvent(event: Partial<AttendanceEventRecord>): AttendanceEventRecord {
    const source = `${event.source || 'device'}`.toLowerCase();
    const status = event.status || '';
    const faceValidationStatus = event.faceValidationStatus || null;
    const employee = this.employees.find((item) => item.employeeId === event.employeeId);
    return {
      attendanceEventId: event.attendanceEventId || crypto.randomUUID(),
      center: event.center || '',
      employeeId: event.employeeId || '',
      employeeNumber: event.employeeNumber || employee?.employeeNumber || '-',
      companiesRecordId: event.companiesRecordId || '',
      orgUnitId: event.orgUnitId || null,
      orgPath: event.orgPath || null,
      source,
      eventType: event.eventType || '',
      normalizedEventType: event.normalizedEventType || null,
      eventAt: event.eventAt || '',
      timezone: event.timezone || 'America/Cancun',
      deviceId: event.deviceId || null,
      deviceVersion: event.deviceVersion || null,
      lat: event.lat ?? null,
      lng: event.lng ?? null,
      accuracy: event.accuracy ?? null,
      faceValidationStatus,
      status,
      rawPayload: event.rawPayload || null,
      isActive: event.isActive !== false,
      employeeName: event.employeeName || employee?.fullName || event.employeeNumber || '-',
      employeeLabel: event.employeeName || employee?.fullName || event.employeeNumber || '-',
      sourceLabel: this.t(`attendanceEventsCatalog.sources.${source}`),
      eventTypeLabel: event.eventType ? this.t(`attendanceEventsCatalog.eventTypes.${event.eventType}`) : '-',
      normalizedEventTypeLabel: event.normalizedEventType ? this.t(`attendanceEventsCatalog.eventTypes.${event.normalizedEventType}`) : '-',
      eventAtLabel: this.formatDateTime(event.eventAt, event.timezone || 'America/Cancun'),
      statusLabel: status ? this.t(`attendanceEventsCatalog.statuses.${status}`) : '-',
      faceValidationStatusLabel: faceValidationStatus ? this.t(`attendanceEventsCatalog.faceStatuses.${faceValidationStatus}`) : '-',
      activeLabel: event.isActive !== false ? this.t('common.status.active') : this.t('common.status.inactive'),
    };
  }

  private toTimeString(value: string | Date): string {
    const date = typeof value === 'string' ? new Date(value) : value;
    if (Number.isNaN(date.getTime())) {
      return '';
    }

    const pad = (part: number) => `${part}`.padStart(2, '0');
    return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
  }

  private syncEventAtInputFromForm(): void {
    const parsedDate = this.parseIsoDate(this.form.eventAt) || new Date();
    this.eventAtInput = this.formatDateTimeInput(parsedDate);
    this.eventAtVisibleMonth = this.startOfMonth(parsedDate);
  }

  private parseIsoDate(value?: string | null): Date | null {
    if (!value) {
      return null;
    }

    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  private parseEventAtInput(value: string): Date | null {
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }

    const localizedMatch = trimmed.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})(?:\s+(\d{1,2}):(\d{2}))?$/);
    if (localizedMatch) {
      const [, dayText, monthText, yearText, hourText = '0', minuteText = '0'] = localizedMatch;
      const parsed = new Date(
        Number(yearText),
        Number(monthText) - 1,
        Number(dayText),
        Number(hourText),
        Number(minuteText),
        0,
        0
      );
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }

    const isoLikeMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{2}):(\d{2}))?$/);
    if (isoLikeMatch) {
      const [, yearText, monthText, dayText, hourText = '0', minuteText = '0'] = isoLikeMatch;
      const parsed = new Date(
        Number(yearText),
        Number(monthText) - 1,
        Number(dayText),
        Number(hourText),
        Number(minuteText),
        0,
        0
      );
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }

    return null;
  }

  private formatDateTimeInput(value: Date): string {
    const pad = (part: number) => `${part}`.padStart(2, '0');
    return `${pad(value.getDate())}/${pad(value.getMonth() + 1)}/${value.getFullYear()} ${pad(value.getHours())}:${pad(value.getMinutes())}`;
  }

  private startOfMonth(value: Date): Date {
    return new Date(value.getFullYear(), value.getMonth(), 1);
  }

  private startOfDay(value: Date): Date {
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }

  private getSelectedEventAtDate(): Date | null {
    return this.parseEventAtInput(this.eventAtInput) || this.parseIsoDate(this.form.eventAt);
  }

  private getEventAtTimeParts(): [number, number] {
    const selectedDate = this.getSelectedEventAtDate();
    if (!selectedDate) {
      return [8, 0];
    }

    return [selectedDate.getHours(), selectedDate.getMinutes()];
  }

  private isSameDay(left: Date, right: Date): boolean {
    return left.getFullYear() === right.getFullYear()
      && left.getMonth() === right.getMonth()
      && left.getDate() === right.getDate();
  }

  private formatDateTime(value?: string | null, timezone?: string): string {
    if (!value) {
      return '-';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    const locale = this.translate.currentLang?.startsWith('en') ? 'en-US' : 'es-MX';
    return new Intl.DateTimeFormat(locale, {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  }

  private formatEventDatePart(part: 'date' | 'time'): string {
    const value = this.selectedEvent?.eventAt;
    if (!value) {
      return '-';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    const locale = this.translate.currentLang?.startsWith('en') ? 'en-US' : 'es-MX';
    if (part === 'date') {
      return new Intl.DateTimeFormat(locale, {
        timeZone: this.selectedEvent?.timezone || 'America/Cancun',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      }).format(date);
    }
    return new Intl.DateTimeFormat(locale, {
      timeZone: this.selectedEvent?.timezone || 'America/Cancun',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  }

  private isFiniteNumber(value: unknown): value is number {
    return typeof value === 'number' && Number.isFinite(value);
  }

  hasMeaningfulValue(value: unknown): boolean {
    if (value === null || value === undefined) {
      return false;
    }

    if (typeof value === 'string') {
      return value.trim().length > 0;
    }

    if (typeof value === 'number') {
      return Number.isFinite(value);
    }

    if (typeof value === 'boolean') {
      return true;
    }

    if (Array.isArray(value)) {
      return value.length > 0;
    }

    if (typeof value === 'object') {
      return Object.keys(value as Record<string, unknown>).length > 0;
    }

    return true;
  }

  private buildRelatedTrackingRange(eventAt?: string | null): { dateFrom: Date; dateTo: Date } | null {
    if (!eventAt) {
      return null;
    }
    const eventDate = new Date(eventAt);
    if (Number.isNaN(eventDate.getTime())) {
      return null;
    }
    return {
      dateFrom: new Date(eventDate.getTime() - AttendanceEventDetailComponent.RELATED_TRACKING_WINDOW_MS),
      dateTo: new Date(eventDate.getTime() + AttendanceEventDetailComponent.RELATED_TRACKING_WINDOW_MS),
    };
  }

  private renderRelatedTrackingMap(): void {
    const container = this.relatedTrackingMapRef?.nativeElement;
    if (!container || !this.isViewMode) {
      return;
    }

    if (!this.relatedTrackingMap) {
      this.relatedTrackingMap = L.map(container, {
        zoomControl: true,
        attributionControl: true,
      }).setView(this.defaultMapCenter, 13);

      this.relatedTrackingTileLayer = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
        attribution: '&copy; OpenStreetMap contributors',
      });
      this.relatedTrackingTileLayer.addTo(this.relatedTrackingMap);
    }

    for (const layer of this.relatedTrackingLayers) {
      layer.remove();
    }
    this.relatedTrackingLayers = [];

    const bounds = L.latLngBounds([]);

    this.relatedTrackingRoutes.forEach((group) => {
      if (!group.points.length) {
        return;
      }

      const latLngs = group.points.map((point) => {
        const latLng = L.latLng(point.lat, point.lng);
        bounds.extend(latLng);
        return latLng;
      });

      const polyline = L.polyline(latLngs, {
        color: AttendanceEventDetailComponent.RELATED_ROUTE_COLOR,
        weight: 4,
        opacity: 0.84,
      }).addTo(this.relatedTrackingMap!);
      this.relatedTrackingLayers.push(polyline);

      group.points.forEach((point, index) => {
        const marker = L.circleMarker([point.lat, point.lng], {
          radius: index === 0 || index === group.points.length - 1 ? 7 : 5,
          color: AttendanceEventDetailComponent.RELATED_ROUTE_COLOR,
          weight: 2,
          fillColor: '#ffffff',
          fillOpacity: 1,
        }).addTo(this.relatedTrackingMap!);

        marker.bindPopup(`
          <div class="attendance-event-map-popup">
            <strong>${group.employeeNumber}</strong><br/>
            ${this.formatDateTime(point.trackedAt)}<br/>
            ${this.t('attendanceEventsCatalog.fields.accuracy')}: ${point.accuracy ?? '-'}<br/>
            ${this.t('attendanceEventsCatalog.fields.deviceId')}: ${point.deviceId || '-'}
          </div>
        `);
        this.relatedTrackingLayers.push(marker);
      });
    });

    if (this.selectedEvent?.lat !== null && this.selectedEvent?.lat !== undefined && this.selectedEvent?.lng !== null && this.selectedEvent?.lng !== undefined) {
      const eventLatLng = L.latLng(this.selectedEvent.lat, this.selectedEvent.lng);
      bounds.extend(eventLatLng);
      const eventMarker = L.circleMarker([this.selectedEvent.lat, this.selectedEvent.lng], {
        radius: 8,
        color: AttendanceEventDetailComponent.EVENT_MARKER_COLOR,
        weight: 3,
        fillColor: '#ffffff',
        fillOpacity: 1,
      }).addTo(this.relatedTrackingMap!);

      eventMarker.bindPopup(`
        <div class="attendance-event-map-popup">
          <strong>${this.t('attendanceEventsCatalog.relatedTracking.eventMarkerTitle')}</strong><br/>
          ${this.formatDateTime(this.selectedEvent.eventAt)}<br/>
          ${this.t('attendanceEventsCatalog.fields.eventType')}: ${this.selectedEvent.eventTypeLabel}<br/>
          ${this.t('attendanceEventsCatalog.fields.deviceId')}: ${this.selectedEvent.deviceId || '-'}
        </div>
      `);
      this.relatedTrackingLayers.push(eventMarker);
    }

    if (bounds.isValid()) {
      this.relatedTrackingMap.fitBounds(bounds.pad(0.12));
    } else {
      this.relatedTrackingMap.setView(this.defaultMapCenter, 13);
    }

    setTimeout(() => this.relatedTrackingMap?.invalidateSize(), 0);
  }

  private hasAccess(action: 'create' | 'read' | 'update' | 'delete' | 'access'): boolean {
    return this.authorizationService.hasAccess({ resource: 'attendanceEvents', action });
  }

  private showNotification(
    colorName: string,
    text: string,
    placementFrom: MatSnackBarVerticalPosition,
    placementAlign: MatSnackBarHorizontalPosition
  ): void {
    this.snackBar.open(text, '', {
      duration: 2500,
      verticalPosition: placementFrom,
      horizontalPosition: placementAlign,
      panelClass: colorName,
    });
  }

  private t(key: string, params?: Record<string, unknown>): string {
    return this.translate.instant(key, params);
  }

  @HostListener('document:click', ['$event'])
  handleDocumentClick(event: MouseEvent): void {
    if (!this.isEventAtPickerOpen) {
      return;
    }

    const target = event.target as Node | null;
    if (target && this.eventAtPickerShellRef?.nativeElement.contains(target)) {
      return;
    }

    this.closeEventAtPicker();
  }
}
